import { inject } from '@angular/core';
import { EMPTY, from, pipe } from 'rxjs';
import { catchError, switchMap, tap } from 'rxjs/operators';
import { patchState, signalStore, withMethods, withState } from '@ngrx/signals';
import { rxMethod } from '@ngrx/signals/rxjs-interop';
import {
  setAllEntities,
  updateEntity,
  withEntities,
} from '@ngrx/signals/entities';

import { SaypStatement } from '@save-as-you-pay/models/sayp-statement.model';
import { SaypStatementsService } from '@save-as-you-pay/services/sayp-statements.service';

const ui = {
  isLoading: false,
  error: null as string | null,
  isCollectionLoaded: false,
};

export const SaypStatementsStore = signalStore(
  { providedIn: 'root' },
  withState(ui),
  withEntities<SaypStatement>(),
  withMethods((store, svc = inject(SaypStatementsService)) => {
    const streamAllStatements = rxMethod<void>(
      pipe(
        switchMap(() => {
          if (store.isCollectionLoaded()) {
            return EMPTY;
          }
          return svc.streamAllStatements().pipe(
            tap({
              next: statements =>
                patchState(store, setAllEntities(statements), {
                  isCollectionLoaded: true,
                  isLoading: false,
                  error: null,
                }),
              error: () => {},
            }),
            catchError(err => {
              patchState(store, {
                isLoading: false,
                error: err.message ?? 'Failed to stream statements',
              });
              return EMPTY;
            })
          );
        })
      )
    );

    return {
      streamAllStatements,
      loadStatementsData(): void {
        streamAllStatements();
      },

      markStatementDownloaded: rxMethod(
        pipe(
          switchMap((statementId: string) =>
            from(svc.markStatementDownloaded(statementId)).pipe(
              tap({
                next: () =>
                  patchState(store, { isLoading: false, error: null }),
                error: () => {},
              }),
              catchError(err => {
                patchState(store, {
                  error: err.message ?? 'Failed to mark statement downloaded',
                });
                return EMPTY;
              })
            )
          )
        )
      ),

      markStatementDownloadedSuccess(statementId: string): void {
        patchState(
          store,
          updateEntity({
            id: statementId,
            changes: { didDownloadStatements: true },
          })
        );
      },
    };
  })
);
